import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { DENSITIES, type Density } from './useDensity'

export interface SortOption<K extends string> {
  key: K
  labelKey: string
}

interface Props<K extends string> {
  options: ReadonlyArray<SortOption<K>>
  value: K
  onChange: (key: K) => void
  /** Card size; left out where the grid is fixed (the phone). */
  density?: Density
  onDensity?: (d: Density) => void
  /** The trigger's class (the phone header's bigger buttons). */
  triggerClassName?: string
  iconSize?: number
}

/** The main header's Sort button: server-side orders plus the card size (decision 20). */
export function SortMenu<K extends string>({ options, value, onChange, density, onDensity, triggerClassName = 'lib-icon-btn', iconSize = 15 }: Props<K>) {
  const { t } = useTranslation('library')
  const [open, setOpen] = useState(false)
  return (
    <div className="lib-anchor">
      <button type="button" className={triggerClassName} aria-label={t('sort.label')} title={t('sort.label')} onClick={() => setOpen(!open)}>
        <FeatherIcon name="list" size={iconSize} />
      </button>
      {open && (
        <>
          <div className="lib-backdrop" onClick={() => setOpen(false)} />
          <div className="lib-pop lib-menu" role="menu" aria-label={t('sort.label')}>
            <span className="lib-menu-head">{t('sort.label')}</span>
            {options.map((o) => (
              <button
                key={o.key}
                type="button"
                role="menuitemradio"
                aria-checked={o.key === value}
                className="lib-menu-item"
                onClick={() => {
                  onChange(o.key)
                  setOpen(false)
                }}
              >
                <span className="lib-menu-label">{t(o.labelKey)}</span>
                {o.key === value && <FeatherIcon name="check" size={14} className="lib-menu-check" />}
              </button>
            ))}
            {onDensity && <span className="lib-menu-head">{t('density.label')}</span>}
            {onDensity && DENSITIES.map((d) => (
              <button
                key={d}
                type="button"
                role="menuitemradio"
                aria-checked={d === density}
                className="lib-menu-item"
                onClick={() => onDensity(d)}
              >
                <span className="lib-menu-label">{t(`density.${d}`)}</span>
                {d === density && <FeatherIcon name="check" size={14} className="lib-menu-check" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
